import type { JSX, ReactNode } from 'react'

/** Aggregated warning summary (spec §3.1 / finding A2).
 *
 *  Rendered ONCE per tab (the fault tab mounts it in EngineeringDashboard);
 *  panels no longer render their own raw warning walls. Raw lines are
 *  classified into count chips; the lines themselves stay reachable behind
 *  one <details> disclosure. Identical lines count ONCE — the same finding
 *  restated by several reports, not several findings (see classify()).
 *  Classification order matters — first match wins.
 *  INDICATIVE ONLY lines are dropped: the indicative banner owns that message.
 */
export interface WarningSummaryProps { warnings: string[] }

interface ClassifiedWarnings {
  defaultImpedance: number
  noInterruptingRating: number
  couldNotPlace: number
  /** First /loop\(s\)/ line, rendered verbatim, once. */
  loopNotice: string | null
  other: number
  /** Every DISTINCT non-dropped line, first-arrival order, for the details
   *  disclosure. */
  raw: string[]
}

function classify(warnings: string[]): ClassifiedWarnings {
  const c: ClassifiedWarnings = {
    defaultImpedance: 0,
    noInterruptingRating: 0,
    couldNotPlace: 0,
    loopNotice: null,
    other: 0,
    raw: [],
  }
  // Exact-string dedupe BEFORE bucketing, so the chips AND the raw disclosure
  // are right at every mount site. The fault tab concatenates five reports and
  // study/duty/arc/protection each copy the same _FaultContext.warnings
  // verbatim (services/fault_analysis.py:321,444,608,732), so one defaulted
  // segment arrives as four identical lines — counting raw lines would render
  // "332 segments use default impedance" for 83 defaulted segments. Dedupe is
  // safe because every line embeds its own segment/device id
  // ("Segment {gis_id}: …", "Device {name}: …"), so two identical strings are
  // always the same finding restated, never two findings. Set iteration is
  // first-insertion order, so the loop notice and the raw list keep arrival
  // order.
  const unique = Array.from(new Set(warnings))
  for (const w of unique) {
    if (/using default impedance/.test(w)) c.defaultImpedance++
    else if (/no interrupting rating/.test(w)) c.noInterruptingRating++
    else if (/could not place/.test(w)) c.couldNotPlace++
    else if (/loop\(s\)/.test(w)) c.loopNotice = c.loopNotice ?? w
    else if (/INDICATIVE ONLY/.test(w)) continue // dropped — the banner owns this message
    else c.other++
    c.raw.push(w)
  }
  return c
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
      {children}
    </span>
  )
}

export function WarningSummary({ warnings }: WarningSummaryProps): JSX.Element | null {
  const c = classify(warnings)
  if (c.raw.length === 0) return null
  return (
    <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {c.defaultImpedance > 0 && (
          <Chip>{c.defaultImpedance} segments use default impedance</Chip>
        )}
        {c.noInterruptingRating > 0 && (
          <Chip>{c.noInterruptingRating} devices have no interrupting rating</Chip>
        )}
        {c.couldNotPlace > 0 && (
          <Chip>{c.couldNotPlace} devices could not be placed on the feeder graph</Chip>
        )}
        {c.other > 0 && <Chip>{c.other} other notices</Chip>}
      </div>
      {c.loopNotice !== null && (
        <div className="text-xs text-orange-800">{c.loopNotice}</div>
      )}
      <details>
        <summary className="text-xs text-orange-700 cursor-pointer select-none">
          Show all {c.raw.length} notices
        </summary>
        <ul className="mt-1 space-y-0.5">
          {c.raw.map((w, i) => (
            <li key={i} className="text-xs text-orange-800">{w}</li>
          ))}
        </ul>
      </details>
    </div>
  )
}
